import { Injectable, computed, signal, effect, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { QUIZ_NAVIGATION_ITEMS, QuizNavigationItem } from '../config/quiz-navigation-config';

@Injectable({
  providedIn: 'root'
})
export class QuizNavigationService {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  // Use the shared navigation routes config
  private readonly navigationRoutes = QUIZ_NAVIGATION_ITEMS;

  // Current position in the navigation flow
  private currentIndex = signal<number>(-1);
  private quizGuid = signal<string>('');
  private isNewQuiz = signal<boolean>(false);

  // Computed signals for navigation state
  readonly hasPrevious = computed(() => this.currentIndex() > 0);
  readonly hasNext = computed(() =>
    this.currentIndex() >= 0
      && this.currentIndex() < this.navigationRoutes.length - 1
      && this.isNewQuiz() === false
  );

  readonly previousRoute = computed(() => {
    if (!this.hasPrevious()) return null;
    const route = this.navigationRoutes[this.currentIndex() - 1];
    return this.buildRouterLink(route.path);
  });

  readonly nextRoute = computed(() => {
    if (!this.hasNext()) return null;
    const route = this.navigationRoutes[this.currentIndex() + 1];
    if (route.requiresExistingQuiz && this.isNewQuiz()) return null;
    return this.buildRouterLink(route.path);
  });

  readonly currentRouteLabel = computed(() => {
    if (this.currentIndex() < 0) return '';
    return this.navigationRoutes[this.currentIndex()].label;
  });

  constructor() {
    // Listen for route changes to update current index
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ).subscribe(url => {
      this.updateFromUrl(url);
    });

    // Initial update based on current URL
    this.updateFromUrl(this.router.url);
  }

  updateQuizGuid(guid: string, isNew: boolean): void {
    this.quizGuid.set(guid);
    this.isNewQuiz.set(isNew);
    // Re-evaluate current index after updating guid
    this.updateFromUrl(this.router.url);
  }

  private updateFromUrl(url: string): void {
    const segments = url.split('/');
    const quizzesIndex = segments.findIndex(s => s === 'quizzes');

    if (quizzesIndex !== -1 && quizzesIndex + 1 < segments.length) {
      // Extract quiz guid
      const guid = segments[quizzesIndex + 1];
      this.quizGuid.set(guid);
      this.isNewQuiz.set(guid === 'new');

      // Find the current route section (if any)
      let routePath = '';
      if (quizzesIndex + 2 < segments.length) {
        routePath = segments[quizzesIndex + 2];
      }

      // Find index in navigation routes
      const index = this.navigationRoutes.findIndex(r => r.path === routePath);
      this.currentIndex.set(index !== -1 ? index : 0); // Default to first route if not found
    }
  }

  private buildRouterLink(path: string): string[] {
    // For the quiz details page (empty path), we need to handle it separately
    // to avoid the trailing slash issue
    if (path === '') {
      return ['/quizzes', this.quizGuid()];
    }

    return ['/quizzes', this.quizGuid(), path];
  }
}
